import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader, Lock } from 'lucide-react';
import { ENV } from '@/config/environment';
import { authService } from '@/lib/firebase';
import { removeStorageItem, SELECTED_ORG_STORAGE_KEY } from '@/lib/capacitor';
import { queryPersister } from '@/lib/queryPersister';

const API_BASE_URL = (ENV.API.BASE_URL || '').replace(/\/$/, '');
// The signup routes sit at the gateway root, not under /api — same as the public invoice and
// estimate portals, which derive their base the same way.
const PUBLIC_API_BASE_URL = API_BASE_URL.replace(/\/api$/i, '');

const MIN_PASSWORD_LENGTH = 8;

/**
 * Which password this link needs.
 *
 * `set_password` — a brand-new address, or one whose login is dormant. They choose a password and
 * the server creates or resurrects the account.
 * `verify_password` — the address already has a working login, so this is someone who works at one
 * company setting up their own. They confirm with the password they already have, and we prove it
 * to the server by signing in here and forwarding the resulting ID token.
 *
 * Defaults to `set_password` when the field is absent, which is what a server that predates this
 * page returns — so the page can deploy first.
 */
type SignupMode = 'set_password' | 'verify_password';

type PageState =
  | { kind: 'loading' }
  | { kind: 'invalid'; message: string }
  | { kind: 'form'; email: string; companyName: string; mode: SignupMode }
  | { kind: 'submitting'; email: string; companyName: string; mode: SignupMode }
  // signedIn false means the account exists but the convenience sign-in did not happen, so the
  // page must hand over to the login screen instead of claiming it is taking them anywhere.
  | { kind: 'done'; signedIn: boolean };

/** Firebase sign-in failures worth explaining rather than showing as a generic error. */
function signInMessage(code: unknown): string {
  switch (code) {
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'That password does not match this account. Try again, or reset it below.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact your administrator.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a few minutes and try again.';
    case 'auth/network-request-failed':
      return 'We could not reach the server. Please check your connection and try again.';
    default:
      return 'We could not sign you in. Please try again.';
  }
}

/**
 * Confirmation step of self-service signup.
 *
 * The signup form on the marketing site collects no password: nothing exists anywhere until the
 * emailed link lands here, and the password is chosen at the moment the account is actually
 * created. That is what keeps an unconfirmed submission from reserving an email address, and it
 * means no password is ever stored or transported twice.
 *
 * On success the user is signed straight in with the password they just set, so they land in the
 * app rather than on a login screen.
 */
export function SignupCompletePage() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('t') || '').trim();

  const [state, setState] = useState<PageState>({ kind: 'loading' });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Also called after a `signup_mode_changed` conflict, which means the address gained or lost a
  // login while this page was open and the other form is now the right one.
  const loadLink = useCallback(async () => {
    try {
      const response = await fetch(
        `${PUBLIC_API_BASE_URL}/public/signup/${encodeURIComponent(token)}`
      );

      if (response.status === 410) {
        setState({
          kind: 'invalid',
          message:
            'This confirmation link has expired or has already been used. Please sign up again to get a new one.',
        });
        return;
      }

      if (!response.ok) {
        setState({
          kind: 'invalid',
          message: 'We could not find this confirmation link. Please check the link in your email.',
        });
        return;
      }

      const data = await response.json();
      setState({
        kind: 'form',
        email: data.email ?? '',
        companyName: data.company_name ?? '',
        mode: data.mode === 'verify_password' ? 'verify_password' : 'set_password',
      });
    } catch {
      setState({
        kind: 'invalid',
        message: 'We could not reach the server. Please check your connection and try again.',
      });
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setState({ kind: 'invalid', message: 'This link is missing its confirmation code.' });
      return;
    }

    void loadLink();
  }, [token, loadLink]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (state.kind !== 'form') return;

      setError(null);
      setNotice(null);

      const { email, companyName, mode } = state;

      if (mode === 'set_password') {
        if (password.length < MIN_PASSWORD_LENGTH) {
          setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
          return;
        }
        if (password !== confirmPassword) {
          setError('The two passwords do not match.');
          return;
        }
      } else if (!password) {
        setError('Enter your password to confirm.');
        return;
      }

      setState({ kind: 'submitting', email, companyName, mode });

      // The existing-account path proves the password to the server by signing in here and
      // forwarding the ID token, so the password itself never leaves the browser. Doing it before
      // the complete call also means a wrong password costs nothing: the link is untouched.
      let idToken: string | undefined;

      if (mode === 'verify_password') {
        try {
          const { token: firebaseToken } = await authService.signIn(email, password);
          idToken = firebaseToken;
        } catch (signInError) {
          setError(signInMessage((signInError as { code?: string })?.code));
          setState({ kind: 'form', email, companyName, mode });
          return;
        }
      }

      try {
        const response = await fetch(
          `${PUBLIC_API_BASE_URL}/public/signup/${encodeURIComponent(token)}/complete`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(
              mode === 'verify_password' ? { id_token: idToken } : { password }
            ),
          }
        );

        if (response.status === 410) {
          setState({
            kind: 'invalid',
            message: 'This confirmation link is no longer valid. Please sign up again.',
          });
          return;
        }

        if (!response.ok) {
          const body = await response.json().catch(() => null);

          // A double-clicked link: the other request is provisioning right now, so this is
          // worth retrying rather than presenting as a failure.
          if (body?.error_code === 'signup_in_progress') {
            setError('We are still finishing your account. Give it a few seconds and try again.');
          } else if (body?.error_code === 'signup_mode_changed') {
            // The address gained or lost a login while this page was open, so it is showing the
            // wrong form. Re-read the link and let them try again against the right one.
            setPassword('');
            setConfirmPassword('');
            setError('Something changed about this account. Please confirm again below.');
            setState({ kind: 'loading' });
            void loadLink();
            return;
          } else if (body?.error_code === 'organization_email_taken') {
            // An account already exists for this address - most likely this link was superseded
            // by a later signup that completed first. Nothing is wrong; they just need to sign in.
            setState({
              kind: 'invalid',
              message: 'An account already exists for this email address. Please sign in instead.',
            });
            return;
          } else {
            setError(
              body?.details ||
                'We could not finish setting up your account. Please try the link again.'
            );
          }

          setState({ kind: 'form', email, companyName, mode });
          return;
        }

        // Forget which organization this browser last had open. Someone who already belonged to
        // another company still has that one stored, so the app would drop them straight back
        // into it and their brand-new business would look like it was never created. Clearing the
        // selection sends them to the organization picker instead, where both are visible.
        //
        // Awaited: on native this is a Preferences write, and the reload below would otherwise
        // race it.
        await removeStorageItem(SELECTED_ORG_STORAGE_KEY);

        // And drop the persisted query cache. 'my-organizations' is persisted to IndexedDB for a
        // week, so without this the app rehydrates the membership list from BEFORE the signup and
        // renders it as if it were current — showing organizations this person may no longer be
        // in, and missing the one just created. The rest of the persisted cache (jobs, contacts,
        // invoices) belongs to that same stale context, so it goes too.
        try {
          await queryPersister.removeClient();
        } catch {
          // Best effort: a browser with IndexedDB blocked has nothing persisted to go stale.
        }

        // The existing-account path already signed in to produce its proof, so it is done.
        if (mode === 'verify_password') {
          setState({ kind: 'done', signedIn: true });
          window.location.replace('/');
          return;
        }

        // Sign in with the password just set, so the user lands in the app rather than being
        // bounced to a login screen to type it a second time.
        try {
          await authService.signIn(email, password);
        } catch {
          // The account was created and works; only the convenience sign-in failed. Say so and
          // hand them the sign-in screen - silently claiming "taking you to the app" and then
          // going nowhere leaves them stranded on a dead page.
          setState({ kind: 'done', signedIn: false });
          return;
        }

        setState({ kind: 'done', signedIn: true });

        // Full reload so the app boots through its normal auth and organization bootstrap
        // rather than mounting mid-flight from this public route.
        window.location.replace('/');
      } catch {
        setError('We could not reach the server. Please check your connection and try again.');
        setState({ kind: 'form', email, companyName, mode });
      }
    },
    [state, password, confirmPassword, token, loadLink]
  );

  // Someone who has an account but cannot remember the password. The reset goes through the
  // identity provider directly, and the signup link is untouched by it: the lookup never consumes
  // the token and a failed completion releases its lease, so the same email still works
  // afterwards. That is the whole reason this needs no server support.
  const handleForgotPassword = useCallback(async () => {
    if (state.kind !== 'form') return;

    setError(null);

    try {
      await authService.resetPassword(state.email);
      setNotice(
        'We have emailed you a link to reset your password. Set a new one, then come back to this page and enter it.'
      );
    } catch (resetError) {
      setError(signInMessage((resetError as { code?: string })?.code));
    }
  }, [state]);

  return (
    <div className="min-h-screen overflow-y-auto bg-secondary-50 py-10 px-4">
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center gap-3 px-6 sm:px-8 py-6 border-b border-gray-100">
          <div className="rounded-full bg-primary-50 p-2.5">
            <Lock className="h-6 w-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Finish setting up</h1>
            <p className="text-sm text-gray-500">Service Strike</p>
          </div>
        </div>

        <div className="px-6 sm:px-8 py-6">
          {state.kind === 'loading' && (
            <div className="flex items-center gap-3 text-gray-600">
              <Loader className="h-5 w-5 animate-spin" />
              <span>Checking your link…</span>
            </div>
          )}

          {state.kind === 'invalid' && (
            <div className="space-y-4">
              <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900">{state.message}</p>
              </div>
              <a
                href="/"
                className="block w-full text-center rounded-lg bg-primary-600 px-4 py-2.5 font-medium text-white hover:bg-primary-700"
              >
                Go to sign in
              </a>
            </div>
          )}

          {state.kind === 'done' && (
            <div className="space-y-4">
              <div className="flex gap-3 rounded-xl border border-green-200 bg-green-50 p-4">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-medium text-green-900">Your account is ready</p>
                  <p className="text-sm text-green-800">
                    {state.signedIn
                      ? 'Taking you to the app…'
                      : 'Sign in with the email and password you just set.'}
                  </p>
                </div>
              </div>

              {!state.signedIn && (
                <a
                  href="/"
                  className="block w-full text-center rounded-lg bg-primary-600 px-4 py-2.5 font-medium text-white hover:bg-primary-700"
                >
                  Go to sign in
                </a>
              )}
            </div>
          )}

          {(state.kind === 'form' || state.kind === 'submitting') && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1">
                <p className="text-sm text-gray-600">
                  Setting up <strong className="text-gray-900">{state.companyName}</strong>
                </p>
                <p className="text-sm text-gray-500">{state.email}</p>
              </div>

              {state.mode === 'verify_password' && (
                <p className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                  You already have a Service Strike account with this email. Confirm with your
                  existing password and we will add this business alongside it — you can switch
                  between them from inside the app.
                </p>
              )}

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  {state.mode === 'verify_password' ? 'Your password' : 'Choose a password'}
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete={
                    state.mode === 'verify_password' ? 'current-password' : 'new-password'
                  }
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={state.kind === 'submitting'}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50"
                />
                {state.mode === 'set_password' && (
                  <p className="mt-1 text-xs text-gray-500">
                    At least {MIN_PASSWORD_LENGTH} characters.
                  </p>
                )}
              </div>

              {state.mode === 'set_password' && (
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Confirm password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={state.kind === 'submitting'}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50"
                  />
                </div>
              )}

              {notice && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <p className="text-sm text-blue-900">{notice}</p>
                </div>
              )}

              {error && (
                <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={state.kind === 'submitting'}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 font-medium text-white hover:bg-primary-700 disabled:opacity-60"
              >
                {state.kind === 'submitting' && <Loader className="h-4 w-4 animate-spin" />}
                {state.kind === 'submitting'
                  ? 'Setting up your account…'
                  : state.mode === 'verify_password'
                    ? 'Confirm and create'
                    : 'Create my account'}
              </button>

              {state.mode === 'verify_password' && (
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={state.kind === 'submitting'}
                  className="w-full text-center text-sm text-primary-600 hover:text-primary-700 hover:underline disabled:opacity-60"
                >
                  Forgot your password?
                </button>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
